export const statisticsForSocialSciencesCourse = {
  id: "statistics-for-social-sciences-2024",
  title: "Statistics for Social Sciences: Data Analysis & Research Methods",
  shortDescription: "Learn statistical concepts and methods for analyzing social science data and conducting empirical research.",
  description: "Comprehensive introduction to statistical methods used in social science research. Students will learn descriptive and inferential statistics, hypothesis testing, correlation, regression, and how to interpret and present statistical findings in social science contexts.",
  category: "Social Sciences",
  industryCategory: "Social Sciences",
  difficulty: "intermediate",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from statistics professors and social science researchers",
  thumbnailUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand fundamental statistical concepts and terminology",
    "Calculate and interpret descriptive statistics",
    "Conduct hypothesis testing and interpret results",
    "Analyze relationships between variables using correlation and regression",
    "Choose appropriate statistical tests for different research questions",
    "Interpret statistical output from software packages",
    "Present statistical findings clearly and accurately",
    "Understand the role of statistics in social science research"
  ],

  prerequisites: [
    "High school algebra",
    "Basic research methods knowledge",
    "Comfort with mathematical concepts"
  ],

  skills: [
    "Statistical Analysis",
    "Data Interpretation",
    "Hypothesis Testing",
    "Regression Analysis",
    "Research Design",
    "SPSS/R Software",
    "Data Visualization",
    "Report Writing"
  ],

  marketData: {
    averageSalary: 65000,
    salaryRange: {
      min: 45000,
      max: 90000
    },
    jobDemand: 4.5,
    marketTrend: "growing",
    careerPaths: [
      "Data Analyst",
      "Research Assistant",
      "Policy Analyst",
      "Market Research Analyst",
      "Statistical Consultant",
      "Survey Researcher"
    ],
    relatedSkills: [
      "Statistics",
      "Data Analysis",
      "Research Methods",
      "SPSS",
      "R Programming",
      "Survey Design"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "descriptive-statistics",
      title: "Descriptive Statistics",
      description: "Measures of central tendency, variability, and distribution shape",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "intro-statistics",
          title: "Introduction to Statistics",
          description: "Role of statistics in social science research",
          type: "video",
          duration: 30
        },
        {
          id: "central-tendency",
          title: "Measures of Central Tendency and Variability",
          description: "Mean, median, mode, and measures of spread",
          type: "text",
          duration: 45,
          textContent: `# Measures of Central Tendency and Variability

## Understanding Data Distribution

### Measures of Central Tendency

**The Mean (Average)**
- **Definition**: Sum of all values divided by number of observations
- **Formula**: x̄ = Σx / n
- **Best used**: With normally distributed, interval/ratio data
- **Sensitive to**: Extreme values (outliers)
- **Example**: Survey responses on 1-10 scale

**The Median**
- **Definition**: Middle value when data is arranged in order
- **Calculation**: For odd n: middle value; for even n: average of two middle values
- **Best used**: With skewed data or ordinal variables
- **Resistant to**: Outliers
- **Example**: Income data (often skewed)

**The Mode**
- **Definition**: Most frequently occurring value
- **Types**: Unimodal, bimodal, multimodal
- **Best used**: With categorical data or to identify common responses
- **Example**: Most common religious affiliation

### Measures of Variability

**Range**
- **Definition**: Difference between highest and lowest values
- **Formula**: Range = Maximum - Minimum
- **Limitations**: Sensitive to outliers, ignores distribution

**Variance**
- **Definition**: Average squared deviation from the mean
- **Population Formula**: σ² = Σ(x - μ)² / N
- **Sample Formula**: s² = Σ(x - x̄)² / (n - 1)

**Standard Deviation**
- **Definition**: Square root of variance
- **Formula**: σ = √σ² or s = √s²
- **Interpretation**: Average distance from the mean
- **68-95-99.7 Rule**: In normal distribution, approximately 68% of data falls within 1 SD, 95% within 2 SD, 99.7% within 3 SD

### Choosing Appropriate Measures

**Normal Distribution**: Mean and standard deviation
**Skewed Distribution**: Median and interquartile range
**Categorical Data**: Mode and frequencies
**Ordinal Data**: Median and range

Understanding these measures helps describe and compare different groups in social science research.`
        },
        {
          id: "statistics-calculator",
          title: "Statistical Calculator Exercise",
          description: "Practice calculating descriptive statistics",
          type: "math-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "statistics-calculation",
            instructions: "Calculate measures of central tendency and variability for different datasets.",
            datasets: [
              {
                name: "Test Scores",
                data: [78, 85, 92, 76, 88, 94, 82, 79, 91, 87],
                tasks: ["Calculate mean", "Find median", "Determine mode", "Calculate standard deviation"]
              },
              {
                name: "Income Data (in thousands)",
                data: [35, 42, 38, 65, 45, 52, 48, 155, 41, 47],
                tasks: ["Compare mean vs median", "Identify outlier effect", "Calculate range"]
              }
            ],
            hints: [
              "Order the data before finding the median",
              "Consider which measure is most appropriate for each dataset",
              "Notice how outliers affect different measures"
            ]
          }
        }
      ],
      quiz: {
        id: "descriptive-stats-quiz",
        title: "Descriptive Statistics Quiz",
        description: "Test your understanding of descriptive statistics",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which measure of central tendency is most affected by outliers?",
            options: [
              "Mean",
              "Median",
              "Mode",
              "All equally affected"
            ],
            correctAnswer: 0,
            explanation: "The mean is most sensitive to outliers because it includes all values in its calculation, while the median only depends on the middle value(s)."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "The standard deviation measures:",
            options: [
              "The most common value",
              "The middle value",
              "The average distance from the mean",
              "The difference between highest and lowest values"
            ],
            correctAnswer: 2,
            explanation: "Standard deviation measures the average distance of data points from the mean, indicating how spread out the data is."
          }
        ]
      }
    },

    {
      id: "probability-distributions",
      title: "Probability and Statistical Distributions",
      description: "Understanding probability theory and common statistical distributions",
      order: 2,
      estimatedHours: 4.0,
      content: [
        {
          id: "probability-basics",
          title: "Fundamentals of Probability",
          description: "Basic probability concepts and rules",
          type: "video",
          duration: 30
        },
        {
          id: "distributions",
          title: "Statistical Distributions",
          description: "Normal, binomial, and other key distributions in social science",
          type: "text",
          duration: 45,
          textContent: `# Probability and Statistical Distributions

## Fundamentals of Probability

### Basic Probability Concepts

**Probability** is the likelihood that a specific event will occur, expressed as a number between 0 and 1 (or 0% to 100%).

**Key Terminology**
- **Experiment**: A procedure that yields one of several possible outcomes
- **Sample Space**: All possible outcomes of an experiment
- **Event**: A specific outcome or set of outcomes
- **Random Variable**: A numerical outcome of a random phenomenon

**Probability Rules**
- **Addition Rule**: P(A or B) = P(A) + P(B) - P(A and B)
- **Multiplication Rule**: P(A and B) = P(A) × P(B|A)
- **Complement Rule**: P(not A) = 1 - P(A)

### Types of Probability

**Theoretical Probability**
- Based on reasoning about equally likely outcomes
- Example: Probability of rolling a 6 on a fair die = 1/6

**Empirical Probability**
- Based on observed data from experiments or studies
- Example: Probability of rain based on weather records

**Subjective Probability**
- Based on personal judgment or experience
- Example: Probability that a particular candidate will win an election

### Statistical Distributions

**Normal Distribution**

**Characteristics**
- **Bell-shaped**: Symmetric curve
- **Mean = Median = Mode**: All measures of central tendency are equal
- **68-95-99.7 Rule**: Standard deviation rule for normal distributions
- **Continuous**: Can take any value within a range

**Parameters**
- **Mean (μ)**: Center of the distribution
- **Standard Deviation (σ)**: Spread of the distribution

**Applications in Social Science**
- Intelligence test scores
- Height and weight measurements
- Many social attitudes and behaviors (when aggregated)

**Standard Normal Distribution**
- **Mean = 0, Standard deviation = 1**
- **Z-scores**: Number of standard deviations from the mean
- **Formula**: z = (x - μ) / σ
- **Use**: Converting any normal distribution to standard form

### Other Important Distributions

**Binomial Distribution**

**Characteristics**
- **Discrete**: Counts of successes in fixed number of trials
- **Binary outcomes**: Success/failure, yes/no, present/absent
- **Fixed probability**: Same probability of success on each trial
- **Independent trials**: Outcome of one trial doesn't affect others

**Parameters**
- **n**: Number of trials
- **p**: Probability of success on each trial

**Applications**
- Survey responses (agree/disagree)
- Treatment effectiveness (success/failure)
- Voting behavior (support/oppose)

**t-Distribution**

**Characteristics**
- Similar to normal distribution but with heavier tails
- Used when sample size is small (typically n < 30)
- Approaches normal distribution as sample size increases
- Has degrees of freedom parameter (df = n - 1)

**Applications**
- Confidence intervals for small samples
- t-tests for comparing means
- Regression analysis

**Chi-Square Distribution**

**Characteristics**
- Right-skewed distribution
- Only positive values
- Used for categorical data analysis
- Has degrees of freedom parameter

**Applications**
- Goodness-of-fit tests
- Tests of independence
- Comparing observed vs. expected frequencies

### Sampling Distributions

**Central Limit Theorem**

**Key Principle**: The distribution of sample means approaches a normal distribution as sample size increases, regardless of the shape of the population distribution.

**Implications**
- Sample means are normally distributed (for large samples)
- Mean of sample means equals population mean
- Standard error = σ / √n

**Standard Error**
- **Definition**: Standard deviation of the sampling distribution
- **Formula**: SE = σ / √n
- **Interpretation**: Measure of sampling variability

### Confidence Intervals

**Concept**
A range of values that likely contains the true population parameter with a specified level of confidence.

**95% Confidence Interval for Mean**
- **Formula**: x̄ ± (critical value × SE)
- **Interpretation**: If we repeated the study many times, 95% of confidence intervals would contain the true population mean

**Factors Affecting Confidence Interval Width**
- **Confidence level**: Higher confidence = wider interval
- **Sample size**: Larger sample = narrower interval
- **Variability**: More variability = wider interval

### Applications in Social Science Research

**Survey Research**
- Probability sampling techniques
- Margin of error calculations
- Confidence intervals for population estimates

**Experimental Design**
- Random assignment probabilities
- Power analysis for sample size determination
- Expected distributions under null hypothesis

**Data Quality Assessment**
- Expected vs. observed distributions
- Outlier detection using probability rules
- Missing data patterns

**Practical Examples**

**Public Opinion Polling**
- Sample of 1,000 respondents
- 95% confidence interval for support: 52% ± 3%
- Interpretation: True population support is likely between 49% and 55%

**Educational Testing**
- Test scores normally distributed with mean = 500, SD = 100
- Student scores 650: z = (650 - 500) / 100 = 1.5
- Student scored higher than approximately 93% of test-takers

**Medical Research**
- Treatment success rate in clinical trial
- Binomial distribution with n = 100 patients, observed successes = 75
- Calculate confidence interval for true success rate

Understanding probability and distributions provides the foundation for inferential statistics and helps researchers make valid conclusions from sample data about larger populations.`
        },
        {
          id: "probability-calculator",
          title: "Probability Calculator Exercise",
          description: "Practice calculating probabilities and using statistical distributions",
          type: "math-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "probability-calculation",
            instructions: "Calculate probabilities and work with different statistical distributions.",
            problems: [
              {
                type: "Normal Distribution",
                scenario: "IQ scores are normally distributed with mean = 100 and SD = 15",
                questions: [
                  "What percentage of people have IQ scores between 85 and 115?",
                  "What IQ score is at the 90th percentile?",
                  "Convert an IQ of 130 to a z-score"
                ]
              },
              {
                type: "Binomial Distribution",
                scenario: "A survey finds 60% support for a policy. You survey 20 people.",
                questions: [
                  "What's the probability exactly 12 people support it?",
                  "What's the probability at least 15 people support it?",
                  "What's the expected number of supporters?"
                ]
              }
            ],
            hints: [
              "Use the 68-95-99.7 rule for normal distribution problems",
              "Remember that z-scores standardize normal distributions",
              "For binomial problems, identify n and p first"
            ]
          }
        }
      ],
      quiz: {
        id: "probability-distributions-quiz",
        title: "Probability and Distributions Quiz",
        description: "Test your knowledge of probability and statistical distributions",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What does the Central Limit Theorem tell us?",
            options: [
              "All populations are normally distributed",
              "Sample means approach normality as sample size increases",
              "Larger samples always have smaller standard deviations",
              "The population mean equals the sample mean"
            ],
            correctAnswer: 1,
            explanation: "The Central Limit Theorem states that the distribution of sample means approaches a normal distribution as sample size increases, regardless of the population's shape."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "A z-score of 2.0 means the data point is:",
            options: [
              "2 units above the mean",
              "2 standard deviations above the mean",
              "At the 2nd percentile",
              "2 times the standard deviation"
            ],
            correctAnswer: 1,
            explanation: "A z-score of 2.0 indicates the data point is 2 standard deviations above the mean in a standardized normal distribution."
          }
        ]
      }
    },

    {
      id: "sampling-inference",
      title: "Sampling and Statistical Inference",
      description: "Sampling methods, estimation, and making inferences from samples to populations",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "sampling-methods",
          title: "Sampling Techniques",
          description: "Different methods for selecting representative samples",
          type: "video",
          duration: 30
        },
        {
          id: "statistical-inference",
          title: "Making Inferences from Data",
          description: "From sample statistics to population parameters",
          type: "text",
          duration: 45,
          textContent: `# Sampling and Statistical Inference

## Sampling Methods

### Why Sampling Matters

In social science research, we rarely can study entire populations. Instead, we select samples and use statistical inference to draw conclusions about the larger population.

**Key Concepts**
- **Population**: The entire group of interest
- **Sample**: A subset of the population actually studied
- **Parameter**: A numerical characteristic of a population (usually unknown)
- **Statistic**: A numerical characteristic of a sample (calculated from data)

### Types of Sampling

**Probability Sampling**

**Simple Random Sampling**
- Every member of the population has an equal chance of selection
- **Advantages**: Unbiased, representative
- **Disadvantages**: May be impractical for large populations
- **Example**: Using a random number generator to select survey participants

**Systematic Sampling**
- Select every kth member after a random starting point
- **Formula**: k = N/n (where N = population size, n = desired sample size)
- **Advantages**: Easier than simple random sampling
- **Risk**: Bias if there's a pattern in the population list

**Stratified Sampling**
- Divide population into strata, then sample from each stratum
- **Proportional**: Sample size in each stratum proportional to stratum size
- **Disproportional**: Oversample smaller but important groups
- **Advantages**: Ensures representation of all groups
- **Example**: Sampling students by grade level

**Cluster Sampling**
- Select groups (clusters) rather than individuals
- **One-stage**: Study all individuals in selected clusters
- **Multi-stage**: Further sample within selected clusters
- **Advantages**: Cost-effective for geographically dispersed populations
- **Example**: Selecting schools, then students within schools

**Non-Probability Sampling**

**Convenience Sampling**
- Select easily accessible participants
- **Advantages**: Quick and inexpensive
- **Disadvantages**: May not be representative
- **Example**: Surveying people at a shopping mall

**Purposive Sampling**
- Deliberately select participants with specific characteristics
- **Expert sampling**: Selecting knowledgeable individuals
- **Critical case sampling**: Cases that illustrate the phenomenon well
- **Example**: Interviewing policy experts about healthcare reform

**Snowball Sampling**
- Participants refer other potential participants
- **Used for**: Hard-to-reach populations
- **Example**: Studying illegal drug users through referral networks

### Sampling Error and Bias

**Sampling Error**
- **Definition**: Random variation between sample statistics and population parameters
- **Measurement**: Standard error quantifies sampling error
- **Reduction**: Larger samples reduce sampling error

**Sampling Bias**
- **Selection Bias**: Some population members have zero probability of selection
- **Non-response Bias**: Systematic differences between respondents and non-respondents
- **Volunteer Bias**: Self-selected participants differ from general population
- **Coverage Bias**: Sampling frame doesn't include all population members

### Statistical Inference

**Point Estimation**

**Unbiased Estimators**
- Sample mean (x̄) estimates population mean (μ)
- Sample proportion (p̂) estimates population proportion (p)
- Sample variance (s²) estimates population variance (σ²)

**Properties of Good Estimators**
- **Unbiased**: Expected value equals population parameter
- **Consistent**: Approaches population parameter as sample size increases
- **Efficient**: Has smallest possible standard error

**Interval Estimation**

**Confidence Intervals**
- **Purpose**: Provide range of plausible values for population parameter
- **Components**: Point estimate ± margin of error
- **Confidence level**: Probability that interval contains true parameter

**Confidence Interval for Mean (σ known)**
- **Formula**: x̄ ± z(α/2) × (σ/√n)
- **Critical values**: z₀.₀₂₅ = 1.96 for 95% confidence

**Confidence Interval for Mean (σ unknown)**
- **Formula**: x̄ ± t(α/2,df) × (s/√n)
- **Use**: t-distribution when σ is unknown and n < 30

**Confidence Interval for Proportion**
- **Formula**: p̂ ± z(α/2) × √[p̂(1-p̂)/n]
- **Conditions**: np ≥ 5 and n(1-p) ≥ 5

### Factors Affecting Inference Quality

**Sample Size**
- **Larger samples**: Smaller standard errors, narrower confidence intervals
- **Power**: Ability to detect true effects increases with sample size
- **Diminishing returns**: Doubling precision requires quadrupling sample size

**Population Variability**
- **More variable populations**: Require larger samples for same precision
- **Homogeneous populations**: Small samples may be adequate

**Confidence Level**
- **Higher confidence**: Wider intervals (trade-off between precision and confidence)
- **Common levels**: 90%, 95%, 99%

### Sample Size Determination

**For Estimating Means**
- **Formula**: n = (z(α/2) × σ / E)²
- **E**: Desired margin of error
- **Challenge**: Need estimate of σ

**For Estimating Proportions**
- **Formula**: n = (z(α/2))² × p(1-p) / E²
- **Conservative approach**: Use p = 0.5 (maximum variability)

**Practical Considerations**
- **Budget constraints**: Balance statistical requirements with available resources
- **Response rates**: Account for non-response in planning
- **Subgroup analysis**: Larger samples needed for analyzing subgroups

### Applications in Social Science

**Survey Research**
- **Opinion polls**: Estimating population attitudes with confidence intervals
- **Margin of error**: Typically ±3% for national polls
- **Weighting**: Adjusting sample to match population demographics

**Program Evaluation**
- **Pre-post designs**: Comparing outcomes before and after intervention
- **Control groups**: Using comparison groups to estimate treatment effects
- **Effect sizes**: Quantifying practical significance of findings

**Quality Control**
- **Process monitoring**: Using samples to monitor ongoing processes
- **Acceptance sampling**: Deciding whether to accept or reject batches

### Interpretation Guidelines

**Confidence Intervals**
- **Correct interpretation**: "We are 95% confident that the true parameter lies between..."
- **Incorrect interpretation**: "There's a 95% probability the parameter is in this interval"

**Statistical vs. Practical Significance**
- **Statistical significance**: Unlikely to occur by chance alone
- **Practical significance**: Large enough to matter in real-world context
- **Consideration**: Large samples can detect trivial but statistically significant differences

**Generalizability**
- **Target population**: To whom can we generalize results?
- **Sampling frame**: How well does it represent the target population?
- **External validity**: Can findings be applied to other settings or populations?

Understanding sampling and inference allows researchers to make valid generalizations from limited data while quantifying the uncertainty in their conclusions.`
        },
        {
          id: "sampling-simulation",
          title: "Sampling Simulation Exercise",
          description: "Explore how different sampling methods affect results",
          type: "math-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "sampling-simulation",
            instructions: "Compare results from different sampling methods and sample sizes to understand sampling variability.",
            scenarios: [
              {
                population: "University students (N=10,000), 45% support new policy",
                samplingMethods: ["Simple Random", "Stratified by Year", "Cluster by Dorm"],
                sampleSizes: [50, 200, 500, 1000],
                tasks: [
                  "Compare accuracy of different methods",
                  "Observe effect of sample size on precision",
                  "Calculate confidence intervals"
                ]
              },
              {
                population: "City residents, income distribution: Mean=$45,000, SD=$15,000",
                samplingMethods: ["Random", "Convenience (mall)", "Stratified by neighborhood"],
                tasks: [
                  "Estimate mean income with confidence intervals",
                  "Identify potential biases in convenience sampling",
                  "Compare precision across methods"
                ]
              }
            ],
            hints: [
              "Notice how sample size affects the width of confidence intervals",
              "Consider why stratified sampling might be more precise",
              "Think about potential biases in non-probability sampling"
            ]
          }
        }
      ],
      quiz: {
        id: "sampling-inference-quiz",
        title: "Sampling and Inference Quiz",
        description: "Test your knowledge of sampling methods and statistical inference",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which sampling method would be best for ensuring all ethnic groups are represented in a study?",
            options: [
              "Simple random sampling",
              "Systematic sampling", 
              "Stratified sampling",
              "Convenience sampling"
            ],
            correctAnswer: 2,
            explanation: "Stratified sampling divides the population into strata (like ethnic groups) and samples from each, ensuring representation of all groups."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What happens to the width of a confidence interval when sample size increases?",
            options: [
              "It increases",
              "It decreases",
              "It stays the same",
              "It depends on the confidence level"
            ],
            correctAnswer: 1,
            explanation: "As sample size increases, the standard error decreases, making confidence intervals narrower (more precise)."
          }
        ]
      }
    },

    {
      id: "hypothesis-testing",
      title: "Hypothesis Testing",
      description: "Principles of hypothesis testing, p-values, and statistical significance",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "hypothesis-basics",
          title: "Fundamentals of Hypothesis Testing",
          description: "Null and alternative hypotheses, Type I and II errors",
          type: "video",
          duration: 35
        },
        {
          id: "testing-procedures",
          title: "Hypothesis Testing Procedures",
          description: "Steps in hypothesis testing and interpretation of results",
          type: "text",
          duration: 45,
          textContent: `# Hypothesis Testing Procedures

## The Logic of Hypothesis Testing

### Basic Concepts

**Hypothesis testing** is a statistical method for making decisions about population parameters based on sample data. It provides a framework for determining whether observed differences or relationships are likely due to chance or represent real effects.

**Key Components**
- **Null Hypothesis (H₀)**: Statement of no effect or no difference
- **Alternative Hypothesis (H₁ or Hₐ)**: Statement of an effect or difference
- **Test Statistic**: Calculated value that measures evidence against H₀
- **p-value**: Probability of observing results as extreme or more extreme if H₀ is true
- **Significance Level (α)**: Threshold for rejecting H₀ (commonly 0.05)

### Types of Hypotheses

**Null Hypothesis Examples**
- μ = 100 (population mean equals 100)
- μ₁ = μ₂ (two population means are equal)
- p = 0.50 (population proportion equals 50%)
- ρ = 0 (population correlation equals zero)

**Alternative Hypothesis Types**

**Two-tailed (Non-directional)**
- H₁: μ ≠ 100 (mean is not equal to 100)
- Tests for difference in either direction
- More conservative approach

**One-tailed (Directional)**
- H₁: μ > 100 (mean is greater than 100)
- H₁: μ < 100 (mean is less than 100)
- Tests for specific direction of difference
- More powerful when direction is predicted

### The Hypothesis Testing Process

**Step 1: State the Hypotheses**
- Clearly define H₀ and H₁
- Ensure hypotheses are mutually exclusive and exhaustive

**Step 2: Choose Significance Level**
- Common choices: α = 0.05, 0.01, or 0.10
- Consider consequences of Type I error

**Step 3: Select Appropriate Test**
- Based on data type, sample size, and assumptions
- Examples: t-test, z-test, chi-square test

**Step 4: Calculate Test Statistic**
- Use sample data to compute the test statistic
- Compare observed data to what's expected under H₀

**Step 5: Determine p-value**
- Probability of obtaining observed results (or more extreme) if H₀ is true
- Calculated based on test statistic and sampling distribution

**Step 6: Make Decision**
- If p < α: Reject H₀ (statistically significant result)
- If p ≥ α: Fail to reject H₀ (not statistically significant)

**Step 7: Draw Conclusion**
- Interpret results in context of research question
- Consider practical significance, not just statistical significance

### Types of Errors

**Type I Error (α)**
- **Definition**: Rejecting H₀ when it's actually true
- **Consequence**: False positive, claiming an effect that doesn't exist
- **Probability**: Equals the significance level (α)
- **Control**: Set α based on acceptable risk level

**Type II Error (β)**
- **Definition**: Failing to reject H₀ when it's actually false
- **Consequence**: False negative, missing a real effect
- **Probability**: Depends on effect size, sample size, and α
- **Reduction**: Increase sample size or use more powerful tests

**Statistical Power (1 - β)**
- **Definition**: Probability of correctly rejecting a false H₀
- **Factors affecting power**:
  - **Effect size**: Larger effects are easier to detect
  - **Sample size**: Larger samples provide more power
  - **Significance level**: Lower α reduces power
  - **Variability**: Less variability increases power

### Common Hypothesis Tests

**One-Sample Tests**

**One-Sample t-test**
- **Purpose**: Test whether a sample mean differs from a hypothesized value
- **Assumptions**: Normal distribution or large sample size (n ≥ 30)
- **Test statistic**: t = (x̄ - μ₀) / (s/√n)
- **Example**: Testing whether average IQ in a school differs from 100

**One-Sample z-test**
- **Purpose**: Same as t-test but when population standard deviation is known
- **Test statistic**: z = (x̄ - μ₀) / (σ/√n)
- **Less common**: Population σ rarely known in practice

**Two-Sample Tests**

**Independent Samples t-test**
- **Purpose**: Compare means of two independent groups
- **Assumptions**: Normal distributions, independent samples, equal variances
- **Test statistic**: t = (x̄₁ - x̄₂) / (sp√(1/n₁ + 1/n₂))
- **Example**: Comparing test scores between two teaching methods

**Welch's t-test**
- **Purpose**: Same as independent samples t-test but doesn't assume equal variances
- **When to use**: When variances are significantly different
- **More robust**: Better performance when assumptions are violated

**Paired Samples t-test**
- **Purpose**: Compare means of two related measurements
- **Examples**: Before/after measurements, matched pairs
- **Test statistic**: t = d̄ / (sd/√n), where d̄ is mean difference
- **Advantage**: Controls for individual differences

### p-values and Interpretation

**Understanding p-values**
- **Definition**: Probability of obtaining observed results (or more extreme) assuming H₀ is true
- **NOT**: Probability that H₀ is true or false
- **Smaller p-values**: Stronger evidence against H₀

**Common Misinterpretations**
- ❌ "p = 0.05 means there's a 5% chance H₀ is true"
- ❌ "p = 0.03 means the effect is practically important"
- ❌ "p = 0.07 means there's no effect"

**Correct Interpretations**
- ✅ "If H₀ were true, we'd see results this extreme or more 5% of the time"
- ✅ "We have moderate evidence against H₀"
- ✅ "The results are consistent with H₁"

### Effect Size and Practical Significance

**Why Effect Size Matters**
- Statistical significance ≠ practical significance
- Large samples can detect trivial differences
- Small samples might miss important effects

**Common Effect Size Measures**

**Cohen's d**
- **Formula**: d = (x̄₁ - x̄₂) / spooled
- **Interpretation**: 
  - Small: d = 0.2
  - Medium: d = 0.5
  - Large: d = 0.8

**Eta-squared (η²)**
- **Purpose**: Proportion of variance explained
- **Range**: 0 to 1
- **Interpretation**: Percentage of total variance due to treatment

### Multiple Comparisons Problem

**The Issue**
- Conducting many tests increases chance of Type I error
- With α = 0.05, expect 5% false positives even if no effects exist
- Problem worsens with more comparisons

**Solutions**

**Bonferroni Correction**
- **Method**: Use α/k for each test (k = number of comparisons)
- **Conservative**: May increase Type II error rate
- **Simple**: Easy to apply and understand

**False Discovery Rate (FDR)**
- **Method**: Control proportion of false discoveries among rejected hypotheses
- **Less conservative**: Better balance of Type I and II errors
- **Modern approach**: Increasingly popular in research

### Practical Considerations

**Sample Size Planning**
- **Power analysis**: Determine needed sample size before data collection
- **Consider**: Desired power (typically 0.80), expected effect size, α level
- **Trade-offs**: Larger samples cost more but provide better precision

**Assumption Checking**
- **Normality**: Use histograms, Q-Q plots, Shapiro-Wilk test
- **Independence**: Consider research design and data collection
- **Equal variances**: Use Levene's test or examine group standard deviations

**Robust Alternatives**
- **Non-parametric tests**: When assumptions are violated
- **Bootstrap methods**: Computer-intensive alternatives to traditional tests
- **Welch's test**: When equal variances assumption is violated

### Reporting Results

**Essential Components**
- **Test used**: Specify the statistical test
- **Test statistic**: Report calculated value
- **p-value**: Include exact value (not just "p < 0.05")
- **Effect size**: Report magnitude of difference
- **Confidence interval**: Provide range of plausible values
- **Sample size**: Include n for each group

**Example Report**
"An independent samples t-test revealed that Group A (M = 85.3, SD = 12.1, n = 30) scored significantly higher than Group B (M = 78.9, SD = 10.5, n = 28), t(56) = 2.15, p = 0.036, Cohen's d = 0.57, 95% CI [0.4, 12.4]. This represents a medium-sized effect."

Understanding hypothesis testing provides the foundation for making evidence-based conclusions in social science research while avoiding common misinterpretations and pitfalls.`
        },
        {
          id: "hypothesis-testing-practice",
          title: "Hypothesis Testing Practice",
          description: "Work through hypothesis testing scenarios step-by-step",
          type: "math-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "hypothesis-testing",
            instructions: "Complete the hypothesis testing process for different research scenarios.",
            scenarios: [
              {
                context: "Educational Research",
                problem: "A new teaching method claims to improve test scores. Current average is 75. After implementing the method with 25 students, the average is 78.5 with SD = 8.2.",
                steps: [
                  "State null and alternative hypotheses",
                  "Choose significance level (α = 0.05)",
                  "Calculate test statistic",
                  "Find p-value",
                  "Make decision and interpret"
                ]
              },
              {
                context: "Psychology Research",
                problem: "Comparing anxiety levels between two therapy groups. Group A (n=20): M=42.3, SD=7.8. Group B (n=18): M=38.1, SD=6.5.",
                steps: [
                  "Identify appropriate test (independent samples t-test)",
                  "Check assumptions",
                  "Calculate pooled standard deviation",
                  "Compute test statistic and p-value",
                  "Calculate effect size (Cohen's d)"
                ]
              }
            ],
            hints: [
              "Always state hypotheses clearly before testing",
              "Consider whether to use one-tailed or two-tailed tests",
              "Remember to check assumptions before choosing a test",
              "Interpret results in practical context, not just statistical significance"
            ]
          }
        }
      ],
      quiz: {
        id: "hypothesis-testing-quiz",
        title: "Hypothesis Testing Quiz",
        description: "Test your understanding of hypothesis testing concepts and procedures",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is a Type I error?",
            options: [
              "Failing to reject a false null hypothesis",
              "Rejecting a true null hypothesis",
              "Using the wrong statistical test",
              "Having too small a sample size"
            ],
            correctAnswer: 1,
            explanation: "A Type I error occurs when we reject the null hypothesis when it is actually true, leading to a false positive conclusion."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "If p = 0.03 and α = 0.05, what should you conclude?",
            options: [
              "Accept the null hypothesis",
              "Reject the null hypothesis",
              "The test is inconclusive",
              "Increase the sample size"
            ],
            correctAnswer: 1,
            explanation: "Since p (0.03) < α (0.05), we reject the null hypothesis and conclude there is statistically significant evidence for the alternative hypothesis."
          }
        ]
      }
    },

    {
      id: "comparing-groups",
      title: "Comparing Groups: t-tests and ANOVA",
      description: "Statistical tests for comparing means between two or more groups",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "t-tests",
          title: "t-tests for Comparing Means",
          description: "One-sample, independent samples, and paired t-tests",
          type: "video",
          duration: 30
        },
        {
          id: "anova",
          title: "Analysis of Variance (ANOVA)",
          description: "Comparing means across multiple groups",
          type: "text",
          duration: 45,
          textContent: `# Analysis of Variance (ANOVA)

## Introduction to ANOVA

### When to Use ANOVA

**Analysis of Variance (ANOVA)** is used when comparing means across three or more groups. While t-tests compare two means, ANOVA extends this to multiple comparisons while controlling for Type I error.

**Why Not Multiple t-tests?**
- **Inflated Type I Error**: Each test has α = 0.05, but overall α increases with multiple tests
- **Example**: Comparing 4 groups requires 6 t-tests, overall α ≈ 0.26
- **ANOVA Solution**: Controls familywise error rate at desired α level

### Types of ANOVA

**One-Way ANOVA**
- **Purpose**: Compare means across groups on one factor (independent variable)
- **Example**: Comparing test scores across three teaching methods

**Two-Way ANOVA**
- **Purpose**: Examine effects of two factors simultaneously
- **Main effects**: Individual effect of each factor
- **Interaction effect**: Combined effect different from sum of individual effects

**Repeated Measures ANOVA**
- **Purpose**: Compare means across time or conditions within same subjects
- **Example**: Measuring depression scores at baseline, 6 months, and 12 months

## One-Way ANOVA

### Assumptions

**Independence**
- Observations must be independent of each other
- Violated by: Clustering, repeated measures without proper analysis

**Normality**
- Dependent variable should be normally distributed within each group
- **Robust to violations**: Especially with larger sample sizes
- **Check with**: Histograms, Q-Q plots, Shapiro-Wilk tests

**Homogeneity of Variance (Homoscedasticity)**
- Equal variances across all groups
- **Test with**: Levene's test, Bartlett's test
- **Rule of thumb**: Largest SD / Smallest SD < 2

### ANOVA Logic

**Total Variation Decomposition**
- **Total Sum of Squares (SST)**: Total variation in the data
- **Between-Groups Sum of Squares (SSB)**: Variation due to group differences
- **Within-Groups Sum of Squares (SSW)**: Variation within groups (error)
- **Relationship**: SST = SSB + SSW

**F-Statistic**
- **Formula**: F = MSB / MSW
- **MSB**: Mean Square Between = SSB / (k-1)
- **MSW**: Mean Square Within = SSW / (N-k)
- **Interpretation**: Ratio of systematic variance to error variance

### ANOVA Table

| Source | SS | df | MS | F | p |
|--------|----|----|----|----|---|
| Between Groups | SSB | k-1 | MSB | F | p-value |
| Within Groups | SSW | N-k | MSW | | |
| Total | SST | N-1 | | | |

**Degrees of Freedom**
- **Between groups**: k - 1 (where k = number of groups)
- **Within groups**: N - k (where N = total sample size)
- **Total**: N - 1

### Interpreting ANOVA Results

**Null and Alternative Hypotheses**
- **H₀**: μ₁ = μ₂ = μ₃ = ... = μₖ (all means are equal)
- **H₁**: At least one mean is different from others

**Significant F-test**
- **Conclusion**: At least one group mean differs significantly
- **Next step**: Post-hoc tests to identify which groups differ
- **Does not tell us**: Which specific groups are different

**Non-significant F-test**
- **Conclusion**: No evidence that group means differ
- **Interpretation**: Differences could be due to sampling variability

### Effect Size in ANOVA

**Eta-squared (η²)**
- **Formula**: η² = SSB / SST
- **Interpretation**: Proportion of total variance explained by group membership
- **Small**: η² = 0.01, Medium: η² = 0.06, Large: η² = 0.14

**Omega-squared (ω²)**
- **Less biased**: Provides better estimate in small samples
- **Formula**: ω² = (SSB - (k-1) × MSW) / (SST + MSW)

### Post-Hoc Tests

**When Needed**
- After significant ANOVA F-test
- To determine which specific groups differ
- Control for multiple comparisons

**Common Post-Hoc Tests**

**Tukey's HSD (Honestly Significant Difference)**
- **Purpose**: All pairwise comparisons
- **Controls**: Familywise error rate
- **Conservative**: Good balance of Type I and II error control

**Bonferroni Correction**
- **Method**: Divide α by number of comparisons
- **Very conservative**: May miss real differences
- **Simple**: Easy to understand and apply

**Scheffé's Test**
- **Most conservative**: Controls for all possible contrasts
- **Use when**: Conducting many complex comparisons

**Fisher's LSD (Least Significant Difference)**
- **Least conservative**: Only use after significant F-test
- **Liberal**: Higher chance of Type I errors

### Planned Contrasts

**Definition**: Specific comparisons planned before data collection
- **Advantage**: More statistical power than post-hoc tests
- **Requirement**: Based on theoretical predictions
- **Types**: Simple contrasts (one group vs. another) or complex contrasts

**Orthogonal Contrasts**
- **Independent**: Results don't overlap
- **Maximum number**: k - 1 orthogonal contrasts for k groups
- **No correction needed**: Each uses full α level

## Two-Way ANOVA

### Main Effects and Interactions

**Main Effects**
- **Factor A Main Effect**: Overall difference between levels of Factor A
- **Factor B Main Effect**: Overall difference between levels of Factor B
- **Independent**: Each factor's effect averaged across other factor

**Interaction Effect**
- **Definition**: Effect of one factor depends on level of other factor
- **Example**: Teaching method effectiveness varies by student ability level
- **Visual**: Non-parallel lines in interaction plot

**Types of Interactions**

**Ordinal Interaction**
- Lines are not parallel but don't cross
- Same rank order of groups at each level

**Disordinal Interaction**
- Lines cross over
- Rank order of groups changes at different levels

### Interpreting Two-Way ANOVA

**Significant Interaction**
- **Priority**: Focus on interaction rather than main effects
- **Simple Effects Analysis**: Test effect of one factor at each level of other factor
- **Caution**: Main effects may be misleading when interaction is present

**Non-significant Interaction**
- **Safe to interpret**: Main effects are meaningful
- **Additive model**: Effects of factors combine additively

### Assumptions and Alternatives

**When Assumptions are Violated**

**Non-parametric Alternatives**
- **Kruskal-Wallis Test**: Non-parametric one-way ANOVA
- **Friedman Test**: Non-parametric repeated measures ANOVA

**Robust ANOVA**
- **Welch's ANOVA**: Doesn't assume equal variances
- **Brown-Forsythe Test**: Another robust alternative

**Transformations**
- **Log transformation**: For positively skewed data
- **Square root**: For count data
- **Arcsine**: For proportion data

### Practical Considerations

**Sample Size Planning**
- **Power analysis**: Determine needed sample size for desired power
- **Effect size estimation**: Use pilot data or literature estimates
- **Equal group sizes**: Preferred for balanced design

**Multiple Dependent Variables**
- **MANOVA**: Multivariate ANOVA for multiple outcomes
- **Advantages**: Controls Type I error across outcomes
- **Complexity**: More difficult to interpret

### Reporting ANOVA Results

**Essential Information**
- **F-statistic and degrees of freedom**: F(dfbetween, dfwithin)
- **p-value**: Exact value preferred
- **Effect size**: η² or ω²
- **Descriptive statistics**: Means and standard deviations for each group
- **Post-hoc results**: If applicable

**Example**
"A one-way ANOVA revealed significant differences in test scores across teaching methods, F(2, 87) = 12.45, p < 0.001, η² = 0.22. Post-hoc tests using Tukey's HSD indicated that Method A (M = 85.3, SD = 8.1) scored significantly higher than both Method B (M = 78.2, SD = 7.9, p = 0.002) and Method C (M = 76.8, SD = 8.5, p < 0.001). Methods B and C did not differ significantly (p = 0.58)."

## Repeated Measures ANOVA

### When to Use
- **Within-subjects design**: Same participants measured multiple times
- **Advantages**: Controls for individual differences, requires fewer participants
- **Examples**: Pre-test, post-test, follow-up measurements

### Additional Assumptions

**Sphericity**
- **Definition**: Equal variances of differences between all pairs of repeated measures
- **Test**: Mauchly's test of sphericity
- **Corrections**: Greenhouse-Geisser or Huynh-Feldt when violated

### Mixed-Design ANOVA
- **Between-subjects factors**: Different groups of participants
- **Within-subjects factors**: Repeated measures on same participants
- **Complex but powerful**: Can examine both types of effects simultaneously

Understanding ANOVA provides powerful tools for comparing multiple groups while maintaining statistical rigor and controlling for Type I errors in social science research.`
        },
        {
          id: "anova-calculator",
          title: "ANOVA Calculation Exercise",
          description: "Practice calculating and interpreting ANOVA results",
          type: "math-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "anova-analysis",
            instructions: "Analyze data using ANOVA and interpret the results, including effect size and post-hoc comparisons.",
            datasets: [
              {
                name: "Teaching Methods Study",
                description: "Comparing test scores across three different teaching methods",
                groups: [
                  { name: "Method A", data: [85, 78, 92, 88, 76, 91, 83, 87, 79, 84] },
                  { name: "Method B", data: [72, 75, 78, 71, 74, 76, 73, 77, 70, 75] },
                  { name: "Method C", data: [88, 92, 85, 90, 87, 93, 86, 89, 84, 91] }
                ],
                tasks: [
                  "Calculate group means and overall mean",
                  "Compute sum of squares (between, within, total)",
                  "Calculate F-statistic and determine significance",
                  "Compute effect size (eta-squared)",
                  "Identify which groups differ using post-hoc tests"
                ]
              },
              {
                name: "Therapy Effectiveness",
                description: "Comparing depression scores before, during, and after treatment",
                design: "Repeated measures",
                data: [
                  { participant: 1, before: 45, during: 38, after: 28 },
                  { participant: 2, before: 52, during: 41, after: 35 },
                  { participant: 3, before: 39, during: 35, after: 25 }
                ],
                tasks: [
                  "Test for sphericity assumption",
                  "Calculate repeated measures ANOVA",
                  "Interpret within-subjects effects",
                  "Plan appropriate follow-up tests"
                ]
              }
            ],
            hints: [
              "Check assumptions before interpreting ANOVA results",
              "Remember that significant ANOVA only tells you groups differ, not which ones",
              "Consider practical significance along with statistical significance",
              "Use appropriate post-hoc tests to control for multiple comparisons"
            ]
          }
        }
      ],
      quiz: {
        id: "anova-quiz",
        title: "t-tests and ANOVA Quiz",
        description: "Test your knowledge of comparing group means",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Why use ANOVA instead of multiple t-tests when comparing more than two groups?",
            options: [
              "ANOVA is easier to calculate",
              "ANOVA controls for inflated Type I error",
              "ANOVA works better with small samples",
              "ANOVA doesn't require assumptions"
            ],
            correctAnswer: 1,
            explanation: "ANOVA controls the familywise error rate, preventing the inflation of Type I error that occurs when conducting multiple t-tests."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What does a significant F-test in ANOVA tell you?",
            options: [
              "All groups are significantly different from each other",
              "At least one group mean differs significantly from the others",
              "The largest and smallest group means are significantly different",
              "The first and last groups are significantly different"
            ],
            correctAnswer: 1,
            explanation: "A significant F-test indicates that at least one group mean differs significantly from the others, but doesn't specify which groups differ."
          }
        ]
      }
    },

    {
      id: "correlation-regression",
      title: "Correlation and Regression Analysis",
      description: "Examining relationships between variables using correlation and regression",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "correlation",
          title: "Correlation Analysis",
          description: "Measuring the strength and direction of linear relationships",
          type: "video",
          duration: 30
        },
        {
          id: "regression",
          title: "Linear Regression",
          description: "Predicting one variable from another using regression analysis",
          type: "text",
          duration: 50,
          textContent: `# Linear Regression Analysis

## From Correlation to Prediction

### Understanding Regression

**Linear regression** extends correlation analysis by allowing us to predict one variable (dependent/outcome variable) from another variable (independent/predictor variable) using the best-fitting straight line.

**Key Differences from Correlation**
- **Correlation**: Measures strength and direction of relationship
- **Regression**: Creates prediction equation and tests causal models
- **Directionality**: Regression assumes X predicts Y (not vice versa)

### Simple Linear Regression

**The Regression Equation**
- **Formula**: Y = a + bX + e
- **Y**: Dependent (predicted) variable
- **X**: Independent (predictor) variable  
- **a**: Y-intercept (value of Y when X = 0)
- **b**: Slope (change in Y for unit change in X)
- **e**: Error term (difference between observed and predicted)

**Best-Fit Line**
- **Criterion**: Minimizes sum of squared errors (least squares method)
- **Residual**: Difference between observed and predicted values
- **Goal**: Find line that makes residuals as small as possible

### Calculating Regression Coefficients

**Slope (b)**
- **Formula**: b = r × (sy/sx)
- **Alternative**: b = Σ[(X - X̄)(Y - Ȳ)] / Σ(X - X̄)²
- **Interpretation**: Expected change in Y for one-unit increase in X

**Y-Intercept (a)**
- **Formula**: a = Ȳ - bX̄
- **Interpretation**: Predicted value of Y when X = 0
- **Caution**: May not be meaningful if X = 0 is outside data range

**Example Calculation**
Data: X = [2, 4, 6, 8], Y = [3, 7, 8, 12]
- X̄ = 5, Ȳ = 7.5
- sx = 2.58, sy = 3.87
- r = 0.95
- b = 0.95 × (3.87/2.58) = 1.42
- a = 7.5 - 1.42(5) = 0.40
- **Equation**: Y = 0.40 + 1.42X

### Assumptions of Linear Regression

**Linearity**
- Relationship between X and Y is linear
- **Check with**: Scatterplot of X vs Y
- **Violation**: Consider transformation or polynomial regression

**Independence**
- Observations are independent of each other
- **Violation**: Autocorrelation in time series data
- **Check with**: Plot residuals vs. order of data collection

**Homoscedasticity**
- Equal variance of residuals across all values of X
- **Check with**: Plot residuals vs. predicted values
- **Pattern**: Random scatter (good) vs. fan shape (violation)

**Normality of Residuals**
- Residuals are normally distributed
- **Check with**: Histogram or Q-Q plot of residuals
- **Less critical**: For large samples due to Central Limit Theorem

### Goodness of Fit

**R-squared (R²)**
- **Definition**: Proportion of variance in Y explained by X
- **Formula**: R² = 1 - (SSresidual / SStotal)
- **Range**: 0 to 1
- **Interpretation**: 
  - R² = 0.25: X explains 25% of variance in Y
  - Higher R² indicates better fit

**Standard Error of Estimate (SEE)**
- **Formula**: SEE = √[Σ(Y - Ŷ)² / (n-2)]
- **Interpretation**: Average distance of observed Y from regression line
- **Use**: Constructing prediction intervals

### Statistical Inference in Regression

**Testing Significance of Slope**
- **Null hypothesis**: H₀: β = 0 (no linear relationship)
- **Alternative**: H₁: β ≠ 0 (significant linear relationship)
- **Test statistic**: t = b / SEb
- **Degrees of freedom**: df = n - 2

**Confidence Interval for Slope**
- **Formula**: b ± t(α/2, df) × SEb
- **Interpretation**: Range of plausible values for true slope

**Prediction vs. Confidence Intervals**

**Confidence Interval for Mean Y**
- Interval for average Y at a given X value
- Narrower interval
- **Use**: Estimating population mean

**Prediction Interval for Individual Y**
- Interval for individual Y value at given X
- Wider interval (includes individual variation)
- **Use**: Predicting specific observations

### Residual Analysis

**Residual Plots**
- **Residuals vs. Fitted Values**: Check homoscedasticity and linearity
- **Residuals vs. X**: Alternative check for patterns
- **Normal Q-Q Plot**: Check normality of residuals
- **Residuals vs. Order**: Check independence (time series)

**Identifying Outliers and Influential Points**

**Outliers**
- **Definition**: Observations with large residuals
- **Detection**: Standardized residuals > |2| or |3|
- **Impact**: May affect regression line substantially

**Leverage**
- **Definition**: How far X value is from mean of X
- **High leverage**: X values far from X̄
- **Concern**: High leverage points can be influential

**Cook's Distance**
- **Definition**: Measures influence of each observation
- **Interpretation**: Values > 4/n suggest influential points
- **Decision**: Consider removing or investigating influential points

### Multiple Regression (Introduction)

**Multiple Predictors**
- **Equation**: Y = a + b₁X₁ + b₂X₂ + ... + bₖXₖ + e
- **Advantages**: Better prediction, control for confounds
- **Interpretation**: Each slope represents effect holding other predictors constant

**Additional Considerations**
- **Multicollinearity**: High correlations between predictors
- **Model Selection**: Choosing which predictors to include
- **Adjusted R²**: Accounts for number of predictors

### Practical Applications in Social Science

**Examples of Regression Applications**

**Education Research**
- Predicting academic achievement from study hours
- Y = GPA, X = Hours studied per week
- Control variables: Prior GPA, SES, motivation

**Psychology**
- Predicting depression scores from stress levels
- Y = Depression inventory score, X = Perceived stress scale
- Consider: Age, gender, social support as additional predictors

**Economics**
- Predicting income from education level
- Y = Annual income, X = Years of education
- Control for: Experience, geographic location, industry

### Regression Limitations and Cautions

**Correlation ≠ Causation**
- Regression shows association, not causation
- **Third variables**: May explain apparent relationship
- **Reverse causation**: Y might influence X instead

**Extrapolation Dangers**
- Predictions outside range of observed X values unreliable
- **Example**: Predicting income for 25 years education when data only includes 12-20 years

**Assumption Violations**
- **Nonlinearity**: May miss curved relationships
- **Heteroscedasticity**: Reduces efficiency of estimates
- **Non-independence**: Inflates Type I error rates

### Advanced Topics (Brief Overview)

**Polynomial Regression**
- Adding X², X³ terms for curved relationships
- **Caution**: Easy to overfit with higher-order terms

**Logistic Regression**
- For binary dependent variables (yes/no, pass/fail)
- Uses odds ratios instead of linear relationships

**Hierarchical/Nested Data**
- Multilevel modeling for grouped data (students within schools)
- Accounts for clustering in data structure

### Reporting Regression Results

**Essential Components**
- **Equation**: Y = a + bX
- **Significance test**: t-value, df, p-value for slope
- **Effect size**: R² and confidence interval for slope
- **Assumptions**: Note any violations and remedies taken
- **Sample size**: n and any missing data

**Example Report**
"Simple linear regression revealed that study hours significantly predicted GPA, b = 0.15, t(48) = 3.42, p = 0.001, with study hours explaining 19.6% of the variance in GPA, R² = 0.196, 95% CI [0.06, 0.24]. For each additional hour of study per week, GPA increased by 0.15 points on average."

### Software Considerations

**Common Packages**
- **SPSS**: Point-and-click interface, good for beginners
- **R**: Powerful and flexible, steeper learning curve
- **Excel**: Basic regression capabilities, limited diagnostics
- **SAS**: Comprehensive but expensive

**Key Output to Interpret**
- Parameter estimates and standard errors
- t-statistics and p-values
- R² and adjusted R²
- Residual plots and diagnostic statistics
- Confidence and prediction intervals

Understanding regression analysis provides powerful tools for prediction and examining relationships between variables while being aware of assumptions and limitations in interpretation.`
        },
        {
          id: "regression-analysis",
          title: "Regression Analysis Exercise",
          description: "Practice building and interpreting regression models",
          type: "math-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "regression-analysis",
            instructions: "Build regression models to predict outcomes and interpret the results, including assumptions checking.",
            scenarios: [
              {
                name: "Academic Performance Study",
                description: "Predicting final exam scores from midterm scores and study hours",
                data: {
                  dependent: "Final Exam Score (Y)",
                  independent: ["Midterm Score (X1)", "Study Hours per Week (X2)"],
                  sampleSize: 50,
                  correlations: { "X1-Y": 0.72, "X2-Y": 0.58, "X1-X2": 0.31 }
                },
                tasks: [
                  "Create scatterplot and assess linearity",
                  "Calculate simple regression: Y = a + b(X1)",
                  "Interpret slope and y-intercept",
                  "Calculate and interpret R²",
                  "Test significance of slope",
                  "Check regression assumptions"
                ]
              },
              {
                name: "Job Satisfaction Research",
                description: "Examining relationship between salary and job satisfaction",
                data: {
                  dependent: "Job Satisfaction (1-10 scale)",
                  independent: ["Annual Salary (thousands)"],
                  sampleSize: 75,
                  additionalInfo: "Include age and years experience as potential confounds"
                },
                tasks: [
                  "Build simple regression model",
                  "Examine residual plots for assumption violations",
                  "Calculate confidence intervals for predictions",
                  "Consider multiple regression with additional predictors",
                  "Compare model fits using R²"
                ]
              }
            ],
            hints: [
              "Always examine scatterplots before running regression",
              "Check residual plots to verify assumptions",
              "Interpret slopes in context of the variables",
              "Remember that correlation doesn't imply causation"
            ]
          }
        }
      ],
      quiz: {
        id: "correlation-regression-quiz",
        title: "Correlation and Regression Quiz",
        description: "Test your understanding of relationship analysis",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In the regression equation Y = 5 + 2X, what does the slope (2) represent?",
            options: [
              "The value of Y when X equals 0",
              "The expected change in Y for a one-unit increase in X", 
              "The correlation between X and Y",
              "The total variance explained by X"
            ],
            correctAnswer: 1,
            explanation: "The slope in a regression equation represents the expected change in the dependent variable (Y) for each one-unit increase in the independent variable (X)."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What does R² measure in regression analysis?",
            options: [
              "The strength of the correlation",
              "The slope of the regression line",
              "The proportion of variance in Y explained by X",
              "The average prediction error"
            ],
            correctAnswer: 2,
            explanation: "R² (R-squared) measures the proportion of variance in the dependent variable that is explained by the independent variable(s) in the regression model."
          }
        ]
      }
    },

    {
      id: "categorical-data",
      title: "Analyzing Categorical Data: Chi-square Tests",
      description: "Statistical methods for analyzing relationships in categorical data",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "chi-square-intro",
          title: "Introduction to Chi-square Tests",
          description: "When and how to use chi-square tests for categorical data",
          type: "video",
          duration: 30
        },
        {
          id: "chi-square-methods",
          title: "Chi-square Test Procedures",
          description: "Goodness of fit and tests of independence",
          type: "text",
          duration: 45,
          textContent: `# Chi-square Test Procedures

## Understanding Categorical Data Analysis

### When to Use Chi-square Tests

**Categorical Data Characteristics**
- Variables measured in categories or groups
- **Nominal**: Categories with no natural order (gender, race, religion)
- **Ordinal**: Categories with meaningful order (education level, income brackets)
- **Frequency data**: Count of observations in each category

**Chi-square Test Applications**
- **Goodness of fit**: Does sample match expected population distribution?
- **Test of independence**: Are two categorical variables related?
- **Homogeneity**: Do different populations have same distribution?

### Chi-square Goodness of Fit Test

**Purpose**
Test whether observed frequencies match expected frequencies based on a theoretical model or known population distribution.

**Hypotheses**
- **H₀**: Sample comes from population with specified distribution
- **H₁**: Sample does not come from specified distribution

**Test Statistic**
- **Formula**: χ² = Σ[(Observed - Expected)² / Expected]
- **Degrees of freedom**: df = k - 1 (where k = number of categories)
- **Distribution**: Chi-square distribution with df degrees of freedom

**Expected Frequencies**
- Based on theoretical model or known proportions
- **Equal distribution**: E = n/k for each category
- **Specified proportions**: E = n × proportion for each category

**Example: Testing Fair Die**
A die is rolled 60 times with results:
- Face 1: 8 times, Face 2: 12 times, Face 3: 10 times
- Face 4: 15 times, Face 5: 7 times, Face 6: 8 times

Expected frequency for fair die: 60/6 = 10 for each face

**Calculation**:
χ² = (8-10)²/10 + (12-10)²/10 + (10-10)²/10 + (15-10)²/10 + (7-10)²/10 + (8-10)²/10
χ² = 0.4 + 0.4 + 0 + 2.5 + 0.9 + 0.4 = 4.6

df = 6 - 1 = 5
Critical value (α = 0.05): χ²₀.₀₅,₅ = 11.07
Decision: 4.6 < 11.07, fail to reject H₀

### Chi-square Test of Independence

**Purpose**
Test whether two categorical variables are independent (unrelated) or associated.

**Hypotheses**
- **H₀**: Variables are independent (no association)
- **H₁**: Variables are associated (dependent)

**Data Structure: Contingency Table**
\`\`\`
           Variable B
Variable A  B₁   B₂   B₃   Total
A₁          n₁₁  n₁₂  n₁₃   R₁
A₂          n₂₁  n₂₂  n₂₃   R₂
Total       C₁   C₂   C₃    N
\`\`\`

**Expected Frequencies**
- **Formula**: E_ij = (Row total × Column total) / Grand total
- **Logic**: Frequencies expected if variables were independent
- **Requirement**: All expected frequencies ≥ 5 (or at least 80% ≥ 5)

**Test Statistic**
- **Formula**: χ² = ΣΣ[(O_ij - E_ij)² / E_ij]
- **Degrees of freedom**: df = (rows - 1) × (columns - 1)

### Example: Education and Political Affiliation

**Research Question**: Is education level associated with political party preference?

**Data**:
\`\`\`
Education     Democrat  Republican  Independent  Total
High School      45        30         25         100
College          35        40         35         110
Graduate         20        30         40          90
Total           100       100        100         300
\`\`\`

**Expected Frequencies**:
For High School Democrats: E = (100 × 100) / 300 = 33.33
For College Republicans: E = (110 × 100) / 300 = 36.67
(Continue for all cells...)

**Calculation**:
χ² = Σ[(O - E)² / E] for all cells
df = (3-1) × (3-1) = 4

### Assumptions and Requirements

**Sample Size Requirements**
- **Expected frequencies**: All E_ij ≥ 5
- **Alternative rule**: At least 80% of cells with E ≥ 5, none < 1
- **Small samples**: Use Fisher's exact test or combine categories

**Independence Assumption**
- Each observation contributes to only one cell
- **Violation**: Repeated measures or related observations
- **Check**: Consider data collection method

**Random Sampling**
- Sample should be randomly selected from population
- **Impact**: Affects generalizability of results

### Measures of Association

**Phi Coefficient (φ)**
- **For 2×2 tables**: φ = √(χ² / n)
- **Range**: 0 to 1
- **Interpretation**: 
  - 0.10 = small effect
  - 0.30 = medium effect  
  - 0.50 = large effect

**Cramér's V**
- **For larger tables**: V = √[χ² / (n × (min(r,c) - 1))]
- **Range**: 0 to 1
- **Interpretation**: Same as phi coefficient

**Contingency Coefficient (C)**
- **Formula**: C = √[χ² / (χ² + n)]
- **Maximum value**: Depends on table size
- **Less commonly used**: Due to variable maximum

### Post-hoc Analysis

**Significant Chi-square Result**
- Indicates overall association exists
- **Doesn't specify**: Which cells contribute most to association
- **Follow-up needed**: Examine standardized residuals

**Standardized Residuals**
- **Formula**: z = (O - E) / √E
- **Interpretation**: Approximately standard normal distribution
- **Significant**: |z| > 2 suggests important departure from independence
- **Use**: Identify which cells drive the overall association

### Chi-square Test of Homogeneity

**Purpose**
Compare distributions of categorical variable across different populations or groups.

**Example**: Do three different schools have same distribution of student majors?
- Test whether proportion of students in each major is consistent across schools
- **Similar to independence test**: But conceptually different research question

**Procedure**
- Same as test of independence
- **Interpretation focus**: Comparing distributions rather than association

### Alternative Tests and Extensions

**Fisher's Exact Test**
- **When**: Expected frequencies too small for chi-square
- **2×2 tables**: Exact probability calculation
- **Computational**: More intensive than chi-square

**McNemar's Test**
- **Paired categorical data**: Before/after measurements on same subjects
- **Example**: Voting preference before and after debate
- **Focus**: Changes in categories rather than independence

**Cochran's Q Test**
- **Extension**: Multiple paired comparisons
- **Example**: Three different treatments on same subjects

### Practical Considerations

**Effect Size Interpretation**
- **Statistical significance**: May not indicate practical importance
- **Large samples**: Can detect trivial associations
- **Consider**: Magnitude of association using phi or Cramér's V

**Cell Frequency Problems**
- **Sparse tables**: Many cells with low frequencies
- **Solutions**: Combine categories, collect more data, exact tests
- **Caution**: Don't combine categories arbitrarily

**Multiple Comparisons**
- **Many chi-square tests**: Increases Type I error
- **Solutions**: Bonferroni correction, FDR control
- **Planning**: Limit number of tests based on theory

### Reporting Chi-square Results

**Essential Information**
- **Test statistic**: χ² value
- **Degrees of freedom**: df
- **p-value**: Exact value preferred
- **Effect size**: φ or Cramér's V
- **Sample size**: n
- **Frequencies**: Observed (and expected if relevant)

**Example Report**
"A chi-square test of independence revealed a significant association between education level and political party preference, χ²(4, N = 300) = 12.85, p = 0.012, Cramér's V = 0.21. Examination of standardized residuals indicated that graduate-educated individuals were more likely to be Independent (z = 2.3) and less likely to be Democrat (z = -2.1) than expected under independence."

### Common Mistakes and Misconceptions

**Misinterpretation of Results**
- ❌ "Chi-square proves causation"
- ✅ Chi-square shows association, not causation

**Assumption Violations**
- ❌ Using chi-square with continuous data
- ✅ Use appropriate tests for data type

**Expected Frequency Problems**
- ❌ Ignoring small expected frequencies
- ✅ Check requirements before interpreting results

**Multiple Testing**
- ❌ Running many chi-square tests without correction
- ✅ Plan analyses and adjust for multiple comparisons

Understanding chi-square tests provides essential tools for analyzing categorical data and examining relationships between qualitative variables in social science research.`
        },
        {
          id: "chi-square-practice",
          title: "Chi-square Analysis Practice",
          description: "Work through chi-square test calculations and interpretations",
          type: "math-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "chi-square-analysis",
            instructions: "Analyze categorical data using chi-square tests and interpret the results including effect sizes.",
            problems: [
              {
                type: "Goodness of Fit",
                scenario: "Testing whether a six-sided die is fair",
                data: {
                  observed: [12, 8, 15, 9, 11, 5],
                  expected: "Equal frequencies (fair die)",
                  totalRolls: 60
                },
                tasks: [
                  "State null and alternative hypotheses",
                  "Calculate expected frequencies",
                  "Compute chi-square statistic",
                  "Determine degrees of freedom",
                  "Find p-value and make decision"
                ]
              },
              {
                type: "Test of Independence",
                scenario: "Relationship between gender and preferred study method",
                data: {
                  table: [
                    ["", "Group Study", "Individual Study", "Online", "Total"],
                    ["Male", 25, 45, 30, 100],
                    ["Female", 40, 35, 25, 100],
                    ["Total", 65, 80, 55, 200]
                  ]
                },
                tasks: [
                  "Calculate expected frequencies for each cell",
                  "Compute chi-square statistic",
                  "Determine significance with α = 0.05",
                  "Calculate Cramér's V as effect size",
                  "Examine standardized residuals",
                  "Interpret practical significance"
                ]
              }
            ],
            hints: [
              "Check that all expected frequencies are ≥ 5",
              "Remember df = (rows-1) × (columns-1) for independence tests",
              "Examine which cells contribute most to significant chi-square",
              "Consider both statistical and practical significance"
            ]
          }
        }
      ],
      quiz: {
        id: "categorical-data-quiz",
        title: "Categorical Data Analysis Quiz",
        description: "Test your knowledge of chi-square tests and categorical data analysis",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main requirement for expected frequencies in chi-square tests?",
            options: [
              "All expected frequencies must be equal",
              "All expected frequencies should be ≥ 5", 
              "Expected frequencies must be whole numbers",
              "Total expected frequency must equal sample size"
            ],
            correctAnswer: 1,
            explanation: "Chi-square tests require that all expected frequencies be at least 5 to ensure the test statistic follows a chi-square distribution."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In a 3×4 contingency table, what are the degrees of freedom for a chi-square test of independence?",
            options: [
              "3",
              "4", 
              "6",
              "12"
            ],
            correctAnswer: 2,
            explanation: "Degrees of freedom for chi-square test of independence = (rows - 1) × (columns - 1) = (3 - 1) × (4 - 1) = 2 × 3 = 6."
          }
        ]
      }
    },

    {
      id: "nonparametric-tests",
      title: "Non-parametric Tests",
      description: "Statistical tests that don't assume normal distributions",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "nonparametric-intro",
          title: "When to Use Non-parametric Tests",
          description: "Alternatives when parametric assumptions are violated",
          type: "video",
          duration: 30
        },
        {
          id: "nonparametric-methods",
          title: "Common Non-parametric Tests",
          description: "Mann-Whitney U, Wilcoxon, Kruskal-Wallis, and other rank-based tests",
          type: "text",
          duration: 45,
          textContent: `# Non-parametric Statistical Tests

## Understanding Non-parametric Methods

### When Parametric Assumptions Fail

**Parametric Test Requirements**
- **Normal distribution**: Data should be approximately normally distributed
- **Equal variances**: Groups should have similar variability (homoscedasticity)
- **Interval/ratio data**: Meaningful numeric measurements
- **Independence**: Observations should be independent

**When to Consider Non-parametric Alternatives**
- **Severely skewed data**: Cannot be normalized through transformation
- **Outliers**: Extreme values that distort parametric results
- **Ordinal data**: Rankings or ordered categories
- **Small sample sizes**: Central Limit Theorem may not apply
- **Violated assumptions**: After attempts at transformation fail

**Advantages of Non-parametric Tests**
- **Robust**: Less affected by outliers and non-normal distributions
- **Flexible**: Work with ordinal data and unusual distributions
- **No distribution assumptions**: Don't assume specific population distribution
- **Simple**: Often easier to understand conceptually

**Disadvantages**
- **Less powerful**: Generally less likely to detect true effects
- **Information loss**: Convert data to ranks, losing information
- **Limited**: Fewer non-parametric options for complex designs
- **Effect sizes**: More difficult to calculate meaningful effect sizes

### Mann-Whitney U Test

**Purpose**
Non-parametric alternative to independent samples t-test for comparing two independent groups.

**When to Use**
- Comparing medians of two independent groups
- Data is ordinal or continuous but non-normal
- Unequal variances between groups
- Small sample sizes

**Hypotheses**
- **H₀**: Two populations have identical distributions (same medians)
- **H₁**: Two populations have different distributions (different medians)

**Procedure**
1. **Combine and rank**: Pool all observations and assign ranks
2. **Sum ranks**: Calculate sum of ranks for each group
3. **Calculate U statistics**: U₁ and U₂ for each group
4. **Test statistic**: Use smaller U value
5. **Compare to critical value**: Or convert to z-score for large samples

**Formulas**
- **U₁** = n₁n₂ + n₁(n₁+1)/2 - R₁
- **U₂** = n₁n₂ + n₂(n₂+1)/2 - R₂
- **Check**: U₁ + U₂ = n₁n₂

**Example**
Group A scores: 23, 31, 42, 57, 63
Group B scores: 18, 28, 35, 49, 52, 58

**Step 1**: Combine and rank all scores
18(1), 23(2), 28(3), 31(4), 35(5), 42(6), 49(7), 52(8), 57(9), 58(10), 63(11)

**Step 2**: Sum ranks
- Group A ranks: 2, 4, 6, 9, 11; R₁ = 32
- Group B ranks: 1, 3, 5, 7, 8, 10; R₂ = 34

**Step 3**: Calculate U
- U₁ = (5)(6) + 5(6)/2 - 32 = 30 + 15 - 32 = 13
- U₂ = (5)(6) + 6(7)/2 - 34 = 30 + 21 - 34 = 17

**Test statistic**: U = min(13, 17) = 13

### Wilcoxon Signed-Rank Test

**Purpose**
Non-parametric alternative to paired samples t-test for comparing two related measurements.

**When to Use**
- Pre-post comparisons on same subjects
- Matched pairs design
- Data is ordinal or non-normal continuous
- Small sample sizes

**Procedure**
1. **Calculate differences**: For each pair, compute difference (post - pre)
2. **Remove zeros**: Eliminate pairs with zero difference
3. **Rank absolute differences**: Rank |differences| from smallest to largest
4. **Assign signs**: Give each rank the sign of the original difference
5. **Sum positive and negative ranks**: T+ and T-
6. **Test statistic**: Smaller of |T+| or |T-|

**Example: Therapy Effectiveness**
Before: 85, 78, 92, 71, 88
After:  79, 72, 88, 68, 82
Diff:   -6, -6, -4, -3, -6

**Ranking**: |3|=1, |4|=2, |6|=4 (average of ranks 3,4,5)
**Signed ranks**: -1, -4, -2, -1, -4
**Sum**: T- = 12, T+ = 0
**Test statistic**: T = 0

### Kruskal-Wallis Test

**Purpose**
Non-parametric alternative to one-way ANOVA for comparing three or more independent groups.

**When to Use**
- Comparing medians across multiple groups
- Data is ordinal or non-normal continuous
- Unequal variances across groups
- Small sample sizes

**Procedure**
1. **Combine and rank**: Pool all observations and assign ranks
2. **Calculate rank sums**: For each group
3. **Compute test statistic**: H statistic
4. **Compare to chi-square**: df = k - 1 (k = number of groups)

**Formula**
H = [12/(N(N+1))] × Σ[Rⱼ²/nⱼ] - 3(N+1)

Where:
- N = total sample size
- Rⱼ = sum of ranks for group j
- nⱼ = sample size for group j

### Friedman Test

**Purpose**
Non-parametric alternative to repeated measures ANOVA for comparing multiple related measurements.

**When to Use**
- Three or more related measurements (within-subjects)
- Block designs (subjects matched on characteristics)
- Ordinal data or non-normal continuous data

**Example Applications**
- Comparing three different treatments on same patients
- Rating three products by same judges
- Measuring attitude at three time points

**Procedure**
1. **Rank within blocks**: For each subject/block, rank the conditions
2. **Sum ranks**: For each condition across all blocks
3. **Calculate test statistic**: Friedman's χᵣ²
4. **Compare to chi-square**: df = k - 1

### Spearman Rank Correlation

**Purpose**
Non-parametric alternative to Pearson correlation for measuring association between two variables.

**When to Use**
- One or both variables are ordinal
- Relationship is monotonic but not linear
- Presence of outliers affecting Pearson r
- Non-normal distributions

**Procedure**
1. **Rank both variables**: Assign ranks separately
2. **Calculate Pearson r**: On the ranks
3. **Alternative formula**: rs = 1 - [6Σd²/(n(n²-1))]
   - Where d = difference between ranks for each pair

**Interpretation**
- **Range**: -1 to +1 (same as Pearson r)
- **Magnitude**: Strength of monotonic relationship
- **Sign**: Direction of relationship
- **Significance**: Test against H₀: ρs = 0

### Sign Test

**Purpose**
Simplest non-parametric test for comparing two related groups.

**When to Use**
- Paired data with only directional information
- Very small sample sizes
- When Wilcoxon signed-rank assumptions fail

**Procedure**
1. **Determine sign**: For each pair (+ or -)
2. **Count**: Number of positive and negative differences
3. **Test statistic**: Smaller count
4. **Compare to binomial**: Under H₀, P(+) = P(-) = 0.5

**Limitations**
- **Less powerful**: Doesn't use magnitude information
- **Simple**: Only considers direction of change

### Choosing the Right Non-parametric Test

**Decision Tree**

**One Sample**
- **Continuous**: Wilcoxon signed-rank (vs. median)
- **Ordinal**: Sign test

**Two Independent Samples**
- **Continuous/Ordinal**: Mann-Whitney U test

**Two Related Samples**
- **Continuous**: Wilcoxon signed-rank test
- **Ordinal**: Sign test

**Multiple Independent Samples**
- **Continuous/Ordinal**: Kruskal-Wallis test

**Multiple Related Samples**
- **Continuous/Ordinal**: Friedman test

**Correlation**
- **Two variables**: Spearman rank correlation

### Post-hoc Tests for Non-parametric Methods

**After Significant Kruskal-Wallis**
- **Dunn's test**: Controls familywise error rate
- **Mann-Whitney with correction**: Pairwise comparisons
- **Bonferroni adjustment**: Divide α by number of comparisons

**After Significant Friedman Test**
- **Nemenyi test**: All pairwise comparisons
- **Wilcoxon signed-rank**: With Bonferroni correction

### Effect Size for Non-parametric Tests

**r (Rank-biserial correlation)**
- **Formula**: r = Z/√N (where Z is standardized test statistic)
- **Interpretation**: Similar to Pearson r
- **Small**: r = 0.1, Medium: r = 0.3, Large: r = 0.5

**η² (Eta-squared for Kruskal-Wallis)**
- **Formula**: η² = (H - k + 1)/(N - k)
- **Interpretation**: Proportion of variance explained

### Assumptions of Non-parametric Tests

**Common Requirements**
- **Independence**: Observations should be independent
- **Similar distributions**: For comparing medians (shape similarity)
- **Ordinal data**: At least ordinal level of measurement

**Not Assumption-Free**
- Still have assumptions, just fewer and weaker
- **Independence remains critical**
- **Random sampling** still important for generalizability

### Parametric vs. Non-parametric Trade-offs

**Power Comparison**
- **Parametric tests**: More powerful when assumptions are met
- **Non-parametric**: Generally 90-95% as powerful
- **Severe violations**: Non-parametric may be more powerful

**Information Use**
- **Parametric**: Use actual values
- **Non-parametric**: Use ranks (information loss)
- **Robustness**: Non-parametric less affected by extreme values

### Reporting Non-parametric Results

**Essential Information**
- **Test used**: Specify the non-parametric test
- **Test statistic**: Report calculated value
- **p-value**: Include exact value
- **Effect size**: Report appropriate measure
- **Medians**: Report medians instead of means
- **Sample sizes**: Include n for each group

**Example Report**
"A Mann-Whitney U test revealed that Group A (Mdn = 42, IQR = 23-57) scored significantly higher than Group B (Mdn = 35, IQR = 28-52), U = 13, z = -2.31, p = 0.021, r = 0.70. This represents a large effect size."

### Modern Developments

**Bootstrap Methods**
- **Resampling**: Create many samples from original data
- **Distribution-free**: Don't assume specific distributions
- **Confidence intervals**: Empirical confidence intervals
- **Power**: Often more powerful than traditional non-parametric tests

**Robust Statistics**
- **Trimmed means**: Remove extreme values before analysis
- **Winsorizing**: Replace extremes with less extreme values
- **M-estimators**: Robust alternatives to means

Understanding non-parametric methods provides valuable alternatives when parametric assumptions fail, ensuring valid statistical inference across diverse data types and conditions.`
        },
        {
          id: "nonparametric-practice",
          title: "Non-parametric Test Selection",
          description: "Practice choosing and applying appropriate non-parametric tests",
          type: "math-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "nonparametric-selection",
            instructions: "Analyze research scenarios to determine when to use non-parametric tests and which test to apply.",
            scenarios: [
              {
                situation: "Comparing customer satisfaction ratings (1-5 scale) between three different service providers",
                data: "Ordinal data, unequal group sizes, skewed distributions",
                options: ["One-way ANOVA", "Kruskal-Wallis test", "Friedman test", "Mann-Whitney U test"],
                correct: "Kruskal-Wallis test",
                explanation: "Three independent groups with ordinal data - use Kruskal-Wallis"
              },
              {
                situation: "Testing whether a meditation program reduces anxiety scores (before vs. after measurements)",
                data: "Paired data, small sample (n=12), non-normal distribution",
                options: ["Paired t-test", "Wilcoxon signed-rank test", "Mann-Whitney U test", "Sign test"],
                correct: "Wilcoxon signed-rank test",
                explanation: "Paired data with non-normal distribution - use Wilcoxon signed-rank"
              },
              {
                situation: "Examining relationship between class rank and GPA",
                data: "One ordinal variable (rank), one continuous but skewed variable (GPA)",
                options: ["Pearson correlation", "Spearman correlation", "Chi-square test", "Mann-Whitney U test"],
                correct: "Spearman correlation",
                explanation: "Correlation with one ordinal variable - use Spearman correlation"
              }
            ],
            tasks: [
              "Identify the type of data and research design",
              "Check parametric assumptions",
              "Select appropriate non-parametric alternative",
              "Consider effect size measures"
            ],
            hints: [
              "Consider the level of measurement (nominal, ordinal, interval/ratio)",
              "Think about whether groups are independent or related",
              "Check if parametric assumptions are met",
              "Remember that non-parametric tests focus on medians, not means"
            ]
          }
        }
      ],
      quiz: {
        id: "nonparametric-tests-quiz",
        title: "Non-parametric Tests Quiz",
        description: "Test your knowledge of when and how to use non-parametric methods",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which non-parametric test is the alternative to a paired samples t-test?",
            options: [
              "Mann-Whitney U test",
              "Wilcoxon signed-rank test",
              "Kruskal-Wallis test",
              "Friedman test"
            ],
            correctAnswer: 1,
            explanation: "The Wilcoxon signed-rank test is the non-parametric alternative to the paired samples t-test for comparing two related groups."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the main advantage of non-parametric tests?",
            options: [
              "They are more powerful than parametric tests",
              "They provide more detailed information about the data",
              "They are robust to violations of normality assumptions",
              "They always give significant results"
            ],
            correctAnswer: 2,
            explanation: "Non-parametric tests are robust to violations of normality and other parametric assumptions, making them useful when these assumptions are not met."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "data-analysis-project",
      title: "Social Science Data Analysis Project",
      description: "Complete statistical analysis of a social science research question",
      type: "project",
      dueWeeks: 12,
      maxScore: 100,
      instructions: "Choose a social science research question, collect or use existing data, conduct appropriate statistical analyses, and present findings with proper interpretation.",
      submissionFormat: "pdf",
      resources: [
        "Statistical software tutorials",
        "Social science datasets",
        "APA statistical reporting guidelines",
        "Research report templates"
      ]
    }
  ],

  finalExam: {
    id: "statistics-final-exam",
    title: "Statistics for Social Sciences Final Examination",
    description: "Comprehensive examination covering descriptive statistics, hypothesis testing, and statistical interpretation",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "A p-value of 0.03 in a hypothesis test means:",
        options: [
          "There is a 3% chance the null hypothesis is true",
          "There is a 3% chance of getting this result if the null hypothesis is true",
          "The effect size is 3%",
          "97% of the data supports the alternative hypothesis"
        ],
        correctAnswer: 1,
        explanation: "A p-value represents the probability of obtaining the observed results (or more extreme) if the null hypothesis were true."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "Which statistical test would be appropriate for comparing means of three independent groups?",
        options: [
          "t-test",
          "Chi-square test",
          "ANOVA",
          "Correlation"
        ],
        correctAnswer: 2,
        explanation: "ANOVA (Analysis of Variance) is used to compare means across three or more independent groups."
      }
    ]
  },

  quizzes: []
};